import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { registerPortalUser, getAssignedManagers } from '../../services/apiclient';
import { toast } from "sonner";
import { Eye, EyeOff, Check, X as ClearIcon, ChevronDown, Search, User } from 'lucide-react';
import { useRbac } from '../../context';

// Interfaces
export interface Manager {
  id: string;
  username: string;
  email: string;
  phone: string;
  location: string;
  role: string;
  isBlocked: boolean;
  createdAt: string;
  currentManager: {
    id: string;
    username: string;
    email: string;
    phone: string;
  };
  roleHistory: {
    role: string;
    startDate: string;
    endDate: string | null;
    manager: {
      id: string;
      username: string;
      email: string;
    };
  }[];
}

// Custom Manager Dropdown Component
const ManagerDropdown = ({ field, managers, isLoading, managerSearch, setManagerSearch, error }:any) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedManager = managers.find((m:any) => m.id === field.value);

  useEffect(() => {
    const handleClickOutside = (event:any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelectManager = (manager:any) => {
    field.onChange(manager.id);
    setIsOpen(false);
    setManagerSearch('');
  };

  const clearSelection = (e:any) => {
    e.stopPropagation();
    field.onChange('');
    setManagerSearch('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <div
        className={`
          relative flex items-center justify-between w-full h-10 px-3 py-2 
          bg-white border border-gray-300 rounded-md cursor-pointer
          hover:border-gray-400 focus-within:border-[#EC6E51] focus-within:ring-1 focus-within:ring-[#EC6E51]
          transition-colors duration-200
          ${error ? 'border-red-500' : ''}
          ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onClick={() => !isLoading && setIsOpen(!isOpen)}
      >
        <div className="flex items-center flex-1 min-w-0">
          {selectedManager ? (
            <div className="flex items-center space-x-2">
              <div className="flex-shrink-0 w-6 h-6 bg-[#EC6E51] rounded-full flex items-center justify-center">
                <User className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm text-gray-900 truncate">{selectedManager.username}</span>
              {selectedManager.email && (
                <span className="text-xs text-gray-500 truncate">({selectedManager.email})</span>
              )}
            </div>
          ) : (
            <span className="text-sm text-gray-500">
              {isLoading ? 'Loading managers...' : 'Select a manager...'}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-1">
          {selectedManager && !isLoading && (
            <button
              type="button"
              onClick={clearSelection}
              className="p-1 hover:bg-gray-100 rounded-sm transition-colors"
              aria-label="Clear selection"
            >
              <ClearIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
          <ChevronDown 
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`} 
          />
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search managers..."
                value={managerSearch}
                onChange={(e) => setManagerSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
              />
            </div>
          </div>

          {/* Manager List */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-[#EC6E51] rounded-full animate-spin"></div>
                  <span>Loading managers...</span>
                </div>
              </div>
            ) : managers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <User className="w-8 h-8 text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">
                  {managerSearch.trim() 
                    ? `No managers found matching "${managerSearch}"` 
                    : "No managers found for this role"
                  }
                </p>
              </div>
            ) : (
              <div className="py-1">
                {managers.map((manager:any) => (
                  <div
                    key={manager.id}
                    className={`
                      flex items-center px-3 py-2 cursor-pointer transition-colors duration-150
                      hover:bg-gray-50 hover:text-gray-900
                      ${field.value === manager.id ? 'bg-[#EC6E51]/10 text-[#EC6E51]' : 'text-gray-700'}
                    `}
                    onClick={() => handleSelectManager(manager)}
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className={`
                        flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
                        ${field.value === manager.id ? 'bg-[#EC6E51] text-white' : 'bg-gray-100 text-gray-600'}
                      `}>
                        <User className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{manager.username} <span className="text-gray-400 text-xs">({manager.role})</span></p>
                        {manager.email && (
                          <p className="text-xs text-gray-500 truncate">{manager.email}</p>
                        )}
                        
                      </div>
                    </div>
                    {field.value === manager.id && (
                      <Check className="w-4 h-4 text-[#EC6E51] flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Validation schema
const newUserSchema = z.object({
  username: z.string().min(1, "Username is required"),
  email: z.string().email("Valid email is required").optional().or(z.literal("")),
  password: z.string().min(6, "Password must be at least 6 characters"),
  role: z.string().min(1, "Role is required"),
  location: z.string().min(1, "Location is required"),
  assignedManagerUserId: z.string().min(1, "Assigned manager is required"),
  phone: z.string().regex(/^\d{10}$/, "Valid 10-digit phone number is required"),
});

type NewUser = z.infer<typeof newUserSchema>;

const BASE_CREATABLE_ROLES = ['ADMIN', 'FBDE', 'CONSULTANT', 'MODERATOR', 'SHM', 'HM', 'AGENCY', 'HR', "BDE", "TPO", "FREELANCE_COUNSELLOR","MASTER_BDE","FREELANCE_BDE","BDE_INTERN", "INTERNAL_TEAM", "COMPANY"] as const;

/** Roles ADMIN can assign when creating users (includes all three *_ADMIN lead roles). */
const ADMIN_CREATABLE_ROLES: string[] = [
  'ADMIN',
  'SHM_ADMIN',
  'SHM',
  'AGENCY_ADMIN',
  'AGENCY',
  'FBDE_ADMIN',
  'FBDE',
  'CONSULTANT',
  'MODERATOR',
  'HM',
  'HR',
  'BDE',
  'TPO',
  'FREELANCE_COUNSELLOR',
  'MASTER_BDE',
  'FREELANCE_BDE',
  'BDE_INTERN',
  'INTERNAL_TEAM',
  'COMPANY',
];

export const FloatingActions = ({ userAdded, setUserAdded }: { userAdded: boolean, setUserAdded: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const { user: rbacUser, role: rbacRole } = useRbac();
  const roles = useMemo((): string[] => {
    const isAdmin =
      rbacRole === 'ADMIN' ||
      rbacUser?.role === 'ADMIN' ||
      String(rbacUser?.role ?? '').toUpperCase() === 'ADMIN';
    return isAdmin ? ADMIN_CREATABLE_ROLES : [...BASE_CREATABLE_ROLES];
  }, [rbacUser?.role, rbacRole]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [managerSearch, setManagerSearch] = useState("");
  
  const { register, handleSubmit, control, formState: { errors, isSubmitting }, reset, setValue, watch } = useForm<NewUser>({
    resolver: zodResolver(newUserSchema),
    defaultValues: {
      username: "",
      email: "",
      password: "",
      role: "",
      location: "",
      assignedManagerUserId: "",
      phone: "",
    },
  });

  const selectedRole = watch("role");

  // Map role to API parameter - can return string or array of strings
  const getApiRoleParam = (role: string): string | string[] => {
    switch (role) {
      case "SHM":
        return ["SHM_ADMIN", "ADMIN"];
      case "SHM_ADMIN":
      case "AGENCY_ADMIN":
      case "FBDE_ADMIN":
      case "Master BDE":
      case "BDE":
      case "MODERATOR":
      case "FREELANCE_COUNSELLOR":  
      case "MASTER_BDE":
        return "ADMIN";
      case "HM":
        return ["SHM", "SHM_ADMIN", "AGENCY", "FBDE", "FBDE_ADMIN", "CONSULTANT", "ADMIN"];
      case "AGENCY":
        return ["AGENCY_ADMIN", "ADMIN"];
      case "FBDE":
      case "CONSULTANT":
        return ["FBDE_ADMIN", "ADMIN"];
      case "HR": 
        return "HM";
      case "TPO": 
        return ["FBDE", "CONSULTANT", "FREELANCE_COUNSELLOR", "ADMIN"];
      case "FREELANCE_BDE":
        return ["MASTER_BDE","FBDE"];
      case "BDE_INTERN":    
        return ["FREELANCE_BDE"];
      case "INTERNAL_TEAM":
        return "ADMIN"; // Internal team reports to ADMIN
      case "COMPANY":
        return "ADMIN"; // Company users report to ADMIN
      default: 
        return role;
    }
  };

  const fetchManagers = async (role: string, search?: string) => {
    try {
      setIsLoading(true);
      const response = await getAssignedManagers(role, search);
      if (!response || response.length === 0) {
        if (search?.trim()) toast.warning("No managers found matching your search.");
        else toast.warning("No managers found for the selected role.");
        setManagers([]);
      } else {
        setManagers(response);
      }
    } catch (error: any) {
      console.error("Error fetching managers:", error);
      toast.error("Failed to fetch managers. Please try again later.");
      setManagers([]);
    } finally { setIsLoading(false); }
  };

  const fetchManagersForRoles = async (roles: string[], search?: string) => {
    try {
      setIsLoading(true);
      const responses = await Promise.all(
        roles.map(role => getAssignedManagers(role, search))
      );
      
      // Combine and deduplicate results
      const combinedManagers = responses.flat();
      const uniqueManagers = combinedManagers.filter((manager, index, self) => 
        index === self.findIndex(m => m.id === manager.id)
      );
      
      if (uniqueManagers.length === 0) {
        if (search?.trim()) toast.warning("No managers found matching your search.");
        else toast.warning("No managers found for the selected role.");
        setManagers([]);
      } else {
        setManagers(uniqueManagers);
      }
    } catch (error: any) {
      console.error("Error fetching managers for multiple roles:", error);
      toast.error("Failed to fetch managers. Please try again later.");
      setManagers([]);
    } finally { setIsLoading(false); }
  };

  useEffect(() => {
    if (selectedRole) {
      setValue("assignedManagerUserId", "");
      setManagerSearch("");
      const apiRole = getApiRoleParam(selectedRole);
      if (Array.isArray(apiRole)) {
        fetchManagersForRoles(apiRole);
      } else {
        fetchManagers(apiRole);
      }
    } else {
      setManagers([]);
      setValue("assignedManagerUserId", "");
      setManagerSearch("");
    }
  }, [selectedRole]);

  useEffect(() => {
    if (!selectedRole) return;
    const timeout = setTimeout(() => {
      const apiRole = getApiRoleParam(selectedRole);
      if (Array.isArray(apiRole)) {
        fetchManagersForRoles(apiRole, managerSearch);
      } else {
        fetchManagers(apiRole, managerSearch);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [managerSearch, selectedRole]);

  const onSubmit = async (data: NewUser) => {
    try {
      setIsLoading(true);
      // Prepare data with email fallback
      const submitData = {
        ...data,
        email: data.email || '', // Provide empty string if email is undefined
      };
      const response = await registerPortalUser(submitData);
      if (response.status === "success") {
        toast.success(response.message || "User added successfully!");
        reset();
        setUserAdded(true);
        setOpen(false);
      } else throw new Error(response.message || "Unknown error occurred");
    } catch (error: any) {
      setUserAdded(false);
      const msg = error.response?.data?.message || error.message || "An unexpected error occurred";
      toast.error(`Failed to add user: ${msg}`);
    } finally { setIsLoading(false); }
  };

  const handleDialogClose = () => { reset(); setOpen(false); };

  return (
    <div className="flex items-center gap-2">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="default" size="icon" className="px-4 hover:bg-[#EC6E51]/90" style={{ padding: "0px 40px" }}>
            Add User
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Add New User</DialogTitle>
            <DialogDescription>Create a new user account by filling out the form below.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-4">
                {/* Username */}
                <div className="relative">
                  <Input {...register("username")} placeholder=" " className="peer h-10 w-full border border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:ring-0 focus:shadow-none focus:border-2 focus:border-[#EC6E51]" />
                  <Label className="absolute left-3 -top-2.5 text-xs text-gray-600 bg-white px-1 transition-all peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-[#EC6E51]">Username</Label>
                  {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username.message}</p>}
                </div>

                {/* Password */}
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} {...register("password")} placeholder=" " className="peer h-10 w-full border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:border-[#EC6E51]" />
                  <div className="absolute right-3 top-2.5 text-gray-500 cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </div>
                  <Label className="absolute left-3 -top-2.5 text-xs text-gray-600 bg-white px-1 transition-all peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-[#EC6E51]">Password</Label>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                </div>

                {/* Location */}
                <div className="relative">
                  <Input {...register("location")} placeholder=" " className="peer h-10 w-full border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:border-[#EC6E51]" />
                  <Label className="absolute left-3 -top-2.5 text-xs text-gray-600 bg-white px-1 transition-all peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-[#EC6E51]">Location</Label>
                  {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location.message}</p>}
                </div>

                {/* Phone */}
                <div className="relative">
                  <Input type="tel" {...register("phone")} maxLength={10} placeholder=" " className="peer h-10 w-full border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:border-[#EC6E51]" />
                  <Label className="absolute left-3 -top-2.5 text-xs text-gray-600 bg-white px-1 transition-all peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-[#EC6E51]">Phone</Label>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                </div>

                {/* Email */}
                <div className="relative">
                  <Input type="email" {...register("email")} placeholder=" " className="peer h-10 w-full border-gray-300 text-gray-900 placeholder-transparent focus:outline-none focus:border-[#EC6E51]" />
                  <Label className="absolute left-3 -top-2.5 text-xs text-gray-600 bg-white px-1 transition-all peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-[#EC6E51]">Email</Label>
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {/* Role */}
                <div>
                  <Label>Role</Label>
                  <Controller
                    control={control}
                    name="role"
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[min(20rem,70vh)] overflow-y-auto">
                          {roles.map((role) => (
                            <SelectItem key={role} value={role}>
                              {role === "FBDE" ? "Franchise" :
                               role === "CONSULTANT" ? "Consultant" :
                               role === "MODERATOR" ? "Moderator" :
                               role === "HR" ? "HR Recruiter" :
                               role === "AGENCY" ? "Agency" :
                               role === "FREELANCE_COUNSELLOR" ? "Freelance Counsellor" :
                               role === "TPO" ? "TPO" :
                               role === "MASTER_BDE" ? "Master BDE" :
                               role === "FREELANCE_BDE" ? "Freelance BDE" :
                               role === "BDE_INTERN" ? "BDE Intern" :
                               role === "INTERNAL_TEAM" ? "Internal Team" :
                               role === "COMPANY" ? "Company" :
                               role === "SHM_ADMIN" ? "SHM Admin" :
                               role === "AGENCY_ADMIN" ? "Agency Admin" :
                               role === "FBDE_ADMIN" ? "FBDE Admin" :
                               role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role.message}</p>}
                </div>

                {/* Assigned Manager with Custom Dropdown */}
                <div>
                  <Label className="block text-sm font-medium text-gray-700 mb-1">Assigned Manager</Label>
                  <Controller
                    control={control}
                    name="assignedManagerUserId"
                    render={({ field }) => (
                      <ManagerDropdown
                        field={field}
                        managers={managers}
                        isLoading={isLoading}
                        managerSearch={managerSearch}
                        setManagerSearch={setManagerSearch}
                        error={errors.assignedManagerUserId}
                      />
                    )}
                  />
                  {errors.assignedManagerUserId && <p className="text-red-500 text-xs mt-1">{errors.assignedManagerUserId.message}</p>}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <DialogPrimitive.Close>
                <Button variant="outline" onClick={handleDialogClose} disabled={isLoading}>Cancel</Button>
              </DialogPrimitive.Close>
              <Button type="submit" disabled={isSubmitting || isLoading}>
                {isSubmitting || isLoading ? "Adding..." : "Add User"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Custom CSS for animations
const styles = `
@keyframes slide-in {
    0% { transform: translateX(100%); opacity: 0; }
    100% { transform: translateX(0); opacity: 1; }
}

.animate-slide-in {
    animation: slide-in 0.3s ease-in-out forwards;
}

/* Custom scrollbar for dropdown */
.max-h-48::-webkit-scrollbar {
    width: 6px;
}

.max-h-48::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.max-h-48::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.max-h-48::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
`;

// Inject styles into the document
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);